import {
	useEffect, useMemo, useRef,
} from 'react';
import {
	alephToast,
	Button,
	ButtonAppearance,
	ButtonColor,
	CheckIcon,
	Floaty,
	Input, isReddit,
	NotificationToast,
	NotificationType,
	getNewAvatar,
	getRandomMainColor,
	AvatarEntity,
	isSpotify,
} from '@aleph/shared-components';
import { useMutation } from '@apollo/client';
import {
	CreateUserGroupDocument,
	InviteUserToGroupDocument,
	RedditUserGroupsListDocument,
	UserAccountLevel,
	UserGroupRole,
	UserGroupRoleInput,
} from 'wb-reddit/graphql/generated';
import {
	AuthGraphCreateUserGroupDocument,
	AuthGraphUserGroupsListDocument,
	AuthGraphAddUserToUserGroupDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import {
	getTeamAccessEntities,
	parseTeamOwnerFromFromQueryUser,
	parseTeamOwnerFromPlatformUser,
	User,
} from 'wb-reddit/app/routes/teams/utils';
import { useFormik } from 'formik';
import useRedditTeamMutationStore from 'wb-reddit/app/routes/teams/hooks/useRedditTeamMutationStore';
import { object, string } from 'yup';
import ProfileImageUploader, { ProfileImage } from 'wb-reddit/app/common/components/profileImageUploader';
import { sortBy, uniqBy } from 'lodash';
import AccessUserListItem from 'wb-reddit/app/routes/teams/components/accessUserListItem';
import AccessBadge from 'wb-reddit/app/common/components/accessBadge';
import { Placeholder } from 'rsuite';
import { MutatorProps } from 'wb-reddit/app/routes/teams/mutator/container';
import TeamUsersAutocomplete from 'wb-reddit/app/routes/teams/mutator/teamUsersAutocomplete';
import TeamOwnerAutocomplete from 'wb-reddit/app/routes/teams/mutator/teamOwnerAutocomplete';
import { Trans, useTranslation } from 'react-i18next';

const RedditTeamMutator = ({
	updateRow, setShouldShowSaveBeforeExit, beforeClose, row,
}: MutatorProps) => {
	const { t } = useTranslation();

	const [
		createGroup,
		{ loading: creating },
	] = useMutation(isReddit() ? CreateUserGroupDocument : AuthGraphCreateUserGroupDocument, {
		errorPolicy:         'all',
		refetchQueries:      [isReddit() ? RedditUserGroupsListDocument : AuthGraphUserGroupsListDocument],
		onCompleted:         (_, clientOptions) => clientOptions?.client?.resetStore(),
		awaitRefetchQueries: true,
	});
	const [inviteUserToGroup, { loading: invitingUser }] = useMutation(
		isReddit() ? InviteUserToGroupDocument : AuthGraphAddUserToUserGroupDocument,
		{ errorPolicy: 'all' },
	);

	const { setMutatingTeam } = useRedditTeamMutationStore();

	const {
		values,
		handleChange,
		handleBlur,
		setValues,
		setFieldValue,
		touched,
		errors,
		setFieldTouched,
		dirty,
		submitForm,
	} = useFormik({
		initialValues:  row,
		initialTouched: {
			userGroupProfilePicture: !!row?.userGroupProfilePicture,
		},
		validationSchema: object().shape({
			userGroupName: string().required('Team name is required'),
		}),
		onSubmit: async (submitValues, { setErrors }) => {
			if (isReddit()) {
				// Initially the userGroupProfilePicture is undefined,
				// on mount and on username change a new image with user initials is generated
				// which is a ProfileImage object
				const userGroupProfilePicture = (submitValues?.userGroupProfilePicture as unknown as ProfileImage)?.file;

				const mutationResponse = await createGroup({
					variables: {
						...submitValues,
						userGroupProfilePicture,
						userIds: submitValues.users?.filter(
							user => user.userGroupUserRole !== UserGroupRole.Owner,
						)?.map((user) => user?.user?.userId) ?? [],
					},
				});

				// @ts-ignore
				const validationErrors: Record<string, string> = Object.keys(mutationResponse.errors?.[0]?.form ?? {}).reduce((accu, field) => {
					// @ts-ignore
					accu[field] = mutationResponse.errors?.[0]?.form?.[field]?.message ?? 'Unknown error';

					return accu;
				}, {});

				setErrors(validationErrors);

				if (!Object.keys(validationErrors ?? {})?.length && mutationResponse.errors) {
					alephToast(
						<NotificationToast
							type={NotificationType.Error}
							title={t('re: something went wrong while saving the team', 'Something went wrong while saving the team')}
							description={
								mutationResponse?.errors?.[0]?.message
									? t(mutationResponse?.errors?.[0]?.message)
									: (
										<div>
											{
												Object.values(validationErrors).map(error => (
													<div key={error}>{error}</div>
												))
											}
										</div>
									)

							}
						/>,
					);

					return;
				}

				if (!Object.keys(validationErrors).length) {
					if (mutationResponse.data?.createUserGroup) {
						const processedRow = {
							...mutationResponse.data.createUserGroup,
							// @ts-ignore TODO
							users: mutationResponse.data.createUserGroup.users?.map(({ __typename, ...user }) => ({
								...user,
								user: parseTeamOwnerFromFromQueryUser(user.user),
							})),
						};

						updateRow?.({
							// @ts-ignore TODO
							row:      processedRow,
							isCreate: true,
						});
					}
					alephToast(
						<NotificationToast
							type={NotificationType.Success}
							title={
								<Trans
									i18nKey="re: team created successfully"
									defaults="Team <strong>{{name}}</strong> created successfully"
									values={{
										name: values.userGroupName,
									}}
								>
									Team <strong>{values.userGroupName}</strong> created successfully
								</Trans>
							}
						/>,
					);

					setMutatingTeam(undefined);
				}

				return;
			}

			const mutationResponse = await createGroup({
				variables: {
					userGroupName: submitValues.userGroupName,
				},
			});

			if (mutationResponse.errors) {
				alephToast(
					<NotificationToast
						type={NotificationType.Error}
						title={t('re: something went wrong while saving the team', 'Something went wrong while saving the team')}
					/>,
				);

				return;
			}

			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			const members = (submitValues?.users ?? []).filter(item => item?.user?.userId !== currentUser?.userId);

			if (mutationResponse.data?.createUserGroup && !members) {
				updateRow?.({
					// @ts-ignore TODO
					row:      mutationResponse.data.createUserGroup,
					isCreate: true,
				});
			}
			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title={
						<Trans
							i18nKey="re: team created successfully"
							defaults="Team <strong>{{name}}</strong> created successfully"
							values={{
								name: values.userGroupName,
							}}
						>
							Team <strong>{values.userGroupName}</strong> created successfully
						</Trans>
					}
				/>,
			);

			if (members) {
				// eslint-disable-next-line no-restricted-syntax,no-unsafe-optional-chaining
				for (const user of members) {
					try {
						// eslint-disable-next-line no-await-in-loop
						await inviteUserToGroup({
							variables: {
								// @ts-ignore TODO
								userId:      user?.user?.userId ?? '',
								// @ts-ignore TODO
								userGroupId: mutationResponse?.data?.createUserGroup?.userGroupId ?? '',
							},
						});

						alephToast(
							<NotificationToast
								type={NotificationType.Success}
								title={
									<Trans
										i18nKey="re: user invited to team"
										defaults="User <strong>{{userFullName}}</strong> successfully invited to <strong>{{userGroupName}}</strong>"
										values={{
											// @ts-ignore
											userFullName:  `${user?.user?.userName} ${user?.user?.userSurname ?? ''}`,
											userGroupName: submitValues?.userGroupName,
										}}
									>
										{/* @ts-ignore */}
										User <strong>{`${user?.user?.userName} ${user?.user?.userSurname ?? ''}`}</strong>
										&nbsp;successfully invited to <strong>{submitValues.userGroupName}</strong>
									</Trans>
								}
							/>,
						);
					} catch (e) {
						alephToast(
							<NotificationToast
								type={NotificationType.Error}
								title={t('re: something went wrong while inviting user', 'Something went wrong while inviting user')}
							/>,
						);

						return;
					}
				}
			}

			updateRow?.({
				row: {
					// @ts-ignore TODO
					...mutationResponse.data.createUserGroup,
					// @ts-ignore TODO
					userGroupAccess: [
						// @ts-ignore TODO
						// eslint-disable-next-line no-unsafe-optional-chaining
						...mutationResponse?.data?.createUserGroup?.userGroupAccess,
						// @ts-ignore TODO
						// eslint-disable-next-line no-unsafe-optional-chaining
						...members,
					],
				},
				isCreate: true,
			});
			setMutatingTeam(undefined);
		},
	});

	useEffect(() => {
		setMutatingTeam(values);
	}, [values]);

	useEffect(
		() => setShouldShowSaveBeforeExit(dirty),
		[dirty],
	);

	const avatarColor = useRef(getRandomMainColor());

	useEffect(() => {
		if (!touched.userGroupProfilePicture) {
			getNewAvatar({
				entityType: 'Team', background: avatarColor.current, size: 167, iconSize: 167,
			})
				.then((newImage) => {
					setFieldValue('userGroupProfilePicture', newImage);
				});
		}
	}, []);

	const currentUser = useUserMetaData();
	const currPlatformUser = useMemo(() => parseTeamOwnerFromPlatformUser(currentUser), [currentUser]);
	const currOwner = values.users?.find?.(user => user.userGroupUserRole === UserGroupRole.Owner)?.user ?? currPlatformUser as User;

	const canEditGroup = currPlatformUser?.userAccountLevel === UserAccountLevel.Admin || values.users?.some?.(
		user => (
			user.userGroupUserRole === UserGroupRole.Owner || user.userGroupUserRole === UserGroupRole.Admin
		) && user?.user?.userId === currPlatformUser?.userId,
	);

	useEffect(() => {
		setFieldValue(
			'users',
			uniqBy(
				[{
					user:                       currOwner,
					userGroupUserRole:          UserGroupRole.Owner,
					userGroupPendingInviteHash: null,
				}, ...values.users ?? []],
				user => user.user?.userId,
			),
		);
	}, [currOwner]);

	const onUserRoleChanged = (userId: string, userRole: UserGroupRoleInput) => {
		setValues(currValues => ({
			...currValues,
			users: currValues.users?.map((currUser) => {
				if (currUser?.user?.userId === userId) {
					return {
						...currUser,
						userGroupUserRole: userRole as unknown as UserGroupRole,
					};
				}

				return currUser;
			}) ?? [],
		}));
	};

	const onUserDisconnected = (userId: string) => setFieldValue(
		'users',
		values.users?.filter((currUser) => currUser?.user?.userId !== userId) ?? [],
	);

	const anyInvalidTouched = () => Object.keys(touched).some((key) => touched[key] && errors[key]);

	const showDiscard = (touched.userGroupName || touched.salesforceId);

	return (
		<div className="flex flex-1 min-w-full px-[40px] pb-0 pt-[40px] gap-[40px]">
			<div className="flex-1 w-[25%] flex justify-center">
				<ProfileImageUploader
					onUploadProfileImage={newImage => {
						setFieldValue('userGroupProfilePicture', newImage);
						setFieldTouched('userGroupProfilePicture');
					}}
					profileImage={values.userGroupProfilePicture}
					readOnly={!canEditGroup || isSpotify()}
					type="Team"
					avatarEntity={AvatarEntity.Team}
				/>
			</div>
			<div className="flex-[2] min-w-[50%] w-[50%] flex flex-col gap-[24px]
						px-[2px] pb-[150px]"
			>
				<div className="font-semibold text-[14px]">
					{t('re: team personal information', 'Personal information')}
				</div>

				<Input
					id="userGroupName"
					label={t('re: team name', 'Team name')}
					className="!w-full"
					onChange={handleChange}
					onBlur={handleBlur}
					value={values.userGroupName ?? ''}
					validationError={touched.userGroupName ? errors.userGroupName : undefined}
					disabled={!canEditGroup}
				/>

				<TeamOwnerAutocomplete
					value={currOwner}
					disabled
				/>

				{isReddit() && (
					<Input
						id="salesforceId"
						label={t('re: salesforce customer id', 'Salesforce customer ID')}
						onChange={handleChange}
						onBlur={handleBlur}
						value={values.salesforceId ?? ''}
						validationError={touched.salesforceId ? errors.salesforceId : undefined}
						disabled={!canEditGroup}
					/>
				)}

				<div className="font-semibold text-[14px] pt-[16px]">
					{t('re: team access', 'Access')}
				</div>

				<TeamUsersAutocomplete
					row={row}
					setFieldValue={setFieldValue}
					// @ts-ignore
					inviteUserToGroup={inviteUserToGroup}
					owner={currOwner}
				/>

				<div>
					<div className="h-[32px] text-gray-600 text-[11px] items-center px-[8px]
						border-b border-gray-200 flex"
					>
						<div className="flex-1">
							{t('re: team contributor', 'CONTRIBUTOR')}
						</div>
						<div className="px-[8px]">
							{t('re: team domain', 'DOMAIN')}
						</div>
						{

							canEditGroup && (
								<>
									{
										values.userGroupId
										&& <div className="w-[80px] flex justify-center">
											{t('re: team admin', 'TEAM ADMIN')}
										</div>
									}
									<div className="w-[60px] text-center">
										{t('re: team revoke', 'REVOKE')}
									</div>
								</>
							)
						}
					</div>

					{
						uniqBy(
							sortBy(
								values.users ?? [],
								user => user?.userGroupUserRole !== UserGroupRole.Owner,
							),
							user => user?.user?.userId,
						)?.map((user, key) => (
							<AccessUserListItem
								key={key}
								user={user}
								group={row}
								onUserDisconnected={onUserDisconnected}
								hideActions={!canEditGroup}
								onUserRoleChanged={onUserRoleChanged}
							/>
						))
					}

					{
						invitingUser && (
							<div
								className="h-[48px] flex gap-[8px] px-[8px] items-center border-b border-b-gray-200
									last:border-0 hover:bg-gray-100"
							>
								<Placeholder.Graph
									active
									className="!w-[16px] !h-[16px] !min-w-[16px] rounded-full"
								/>

								<Placeholder.Graph
									active
									className="!h-[16px]"
								/>
							</div>
						)
					}
				</div>
			</div>
			<div className="flex-1 w-[25%] flex flex-col gap-[16px]">
				<div className="font-semibold text-[14px]">
					{t('re: team sidebar wise blue', 'WISE.BLUE')}
				</div>

				<div className="flex text-[12px]">
					<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
						<div>
							{t('re: team sidebar id', 'ID')}
						</div>
						<div>
							{t('re: team sidebar access', 'Access')}
						</div>
					</div>
					<div className="text-black flex flex-col gap-[8px]">
						<div className="text-gray-800">
							{values.userGroupId ?? '-'}
						</div>
						<div>
							{
								values?.userGroupId ? <AccessBadge
									accessEntities={getTeamAccessEntities(row)}
								/> : '-'
							}
						</div>
					</div>
				</div>
			</div>
			<Floaty>
				{
					!(creating)	&& showDiscard && (
						<Button
							title={t('sc: discard', 'Discard')}
							data-testid="teams-discard"
							appearance={ButtonAppearance.OUTLINED}
							color={ButtonColor.RED}
							onClick={beforeClose}
						/>
					)
				}

				{
					canEditGroup && (
						<Button
							leftNode={<CheckIcon />}
							title={t('sc: team.create', 'Create team')}
							data-testid="teams-confirm"
							disabled={creating || anyInvalidTouched()}
							loading={creating || invitingUser}
							onClick={submitForm}
							color={ButtonColor.GREEN}
						/>
					)
				}
			</Floaty>
		</div>
	);
};

export default RedditTeamMutator;
